import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import TradingViewDatafeed from "../services/tradingViewDatafeed";
import marketsData from "../utils/markets.json";
import apiService from "../services/apiService";
import { useDuelWebSocket } from "../hooks/useDuelWebSocket";
import { parseHttpDate } from "../utils/helper";
import "./DuelChart.css";

// Error Boundary Component
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Chart Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="dc-error">
          <h3>Chart Error</h3>
          <p>
            Something went wrong with the chart. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="dc-retry-btn"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const DuelChart = () => {
  const { marketId: paramMarketId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Simplified state management
  const [chartState, setChartState] = useState({
    loading: true,
    error: null,
    ready: false,
    retryCount: 0,
  });
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedResolution] = useState("1");
  const [duelData, setDuelData] = useState(null);
  const [tradesData, setTradesData] = useState([]);
  const [filteredTradesData, setFilteredTradesData] = useState([]);
  const [tradedMarkets, setTradedMarkets] = useState([]);

  // Refs
  const chartContainerRef = useRef(null);
  const chartWidgetContainerRef = useRef(null);
  const tvWidgetRef = useRef(null);
  const datafeedRef = useRef(null);
  const isMountedRef = useRef(true);
  const initializationPromiseRef = useRef(null);
  const [chartContainerId] = useState(
    () => `tv-chart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );
  const [chartWidgetContainerId] = useState(
    () =>
      `tv-chart-inner-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Compute chart size based on container for responsiveness (mobile-friendly)
  const computeChartSize = useCallback(() => {
    const container = chartContainerRef.current;
    if (!container) {
      return { width: 0, height: 0 };
    }

    // Ensure container is visible and has dimensions
    const rect = container.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(container);

    // Get actual dimensions from various sources
    let width = container.clientWidth || rect.width;
    let height = container.clientHeight || rect.height;

    // If dimensions are still 0, try getting from CSS
    if (width === 0) {
      width =
        parseInt(computedStyle.width) || parseInt(computedStyle.minWidth) || 0;
    }
    if (height === 0) {
      height =
        parseInt(computedStyle.height) ||
        parseInt(computedStyle.minHeight) ||
        0;
    }

    // Fallback to viewport-based calculations for mobile
    if (width === 0) {
      width = Math.max(300, window.innerWidth - 24); // Account for padding
    }
    if (height === 0) {
      // For mobile, use a percentage of viewport height, but ensure minimum height
      const viewportHeight = window.innerHeight;
      const ua =
        typeof navigator !== "undefined" && navigator && navigator.userAgent
          ? navigator.userAgent
          : "";
      const isMobile =
        window.innerWidth < 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          ua
        );

      if (isMobile) {
        // In Telegram miniapps, account for the interface
        height = Math.min(500, Math.max(300, viewportHeight * 0.5));
      } else {
        height = Math.min(600, Math.max(320, viewportHeight * 0.6));
      }
    }

    // Ensure minimum dimensions
    width = Math.max(300, width);
    height = Math.max(200, height);

    return { width: Math.round(width), height: Math.round(height) };
  }, []);

  // Available resolutions (commented out since resolution selector is disabled)
  // const resolutions = [
  //   { value: '1', label: '1 Minute' },
  //   { value: '5', label: '5 Minutes' },
  //   { value: '15', label: '15 Minutes' },
  //   { value: '60', label: '1 Hour' },
  //   { value: '240', label: '4 Hours' },
  //   { value: '1D', label: '1 Day' }
  // ];

  // Find market by ID
  const findMarketById = (id) => {
    return marketsData.find((market) => market.market_id === parseInt(id));
  };

  // Simplified TradingView loading check
  const isTradingViewLoaded = useCallback(() => {
    return !!(window.TradingView && window.TradingView.widget);
  }, []);


  const fetchTradeDetails = useCallback(async () => {
    if (!duelData) return;
    try {
      console.log("Fetching trade details from API...");
      const data = await apiService.getTradeDetails(
        duelData.challengerUserId,
        duelData.opponentUserId,
        duelData.id
      );

      // Process orders from both challenger and opponent
      const allOrders = [];

      if (data.challengerOrders && data.challengerOrders.length > 0) {
        data.challengerOrders.forEach((order) => {
          allOrders.push({
            ...order,
            user: duelData.challengerUsername,
            side: order.side || "Trade",
            timestamp: parseHttpDate(order.created_at) || Date.now(),
            trade_id: order._id,
          });
        });
      }

      if (data.opponentOrders && data.opponentOrders.length > 0) {
        data.opponentOrders.forEach((order) => {
          allOrders.push({
            ...order,
            user: duelData.opponentUsername,
            side: order.side || "Trade",
            timestamp: parseHttpDate(order.created_at) || Date.now(),
            trade_id: order._id,
          });
        });
      }

      if (allOrders.length > 0) {
        // console.log('Adding orders from API:', allOrders.length);

        // Sort orders by timestamp (newest first)
        const sortedOrders = allOrders.sort((a, b) => {
          return (b.timestamp || 0) - (a.timestamp || 0);
        });

        if (datafeedRef.current) {
          datafeedRef.current.updateDuelData(duelData, sortedOrders);
        }

        setTradesData(sortedOrders);
        setFilteredTradesData(sortedOrders);
      }
    } catch (error) {
      console.error("Error fetching trade details:", error);
    }
  },[duelData]);

  // Simplified TradingView widget initialization
  const initializeTradingViewWidget = useCallback(async () => {
    if (!isMountedRef.current || !selectedMarket || !isTradingViewLoaded()) {
      return false;
    }

    const containerElement = chartContainerRef.current;
    const widgetContainerElement = chartWidgetContainerRef.current;
    if (!containerElement || !widgetContainerElement) {
      return false;
    }

    try {
      setChartState((prev) => ({ ...prev, loading: true, error: null }));

      // Clean up existing widget
      if (tvWidgetRef.current) {
        try {
          if (typeof tvWidgetRef.current.remove === "function") {
            tvWidgetRef.current.remove();
          }
        } catch (error) {
          console.warn("Widget cleanup warning:", error);
        }
        tvWidgetRef.current = null;
      }

      // Set container IDs
      containerElement.id = chartContainerId;
      widgetContainerElement.id = chartWidgetContainerId;

      // Create datafeed
      datafeedRef.current = new TradingViewDatafeed(
        marketsData,
        duelData,
        tradesData
      );

      const { width, height } = computeChartSize();
      containerElement.style.height = `${height}px`;
      widgetContainerElement.style.width = "100%";
      widgetContainerElement.style.height = "100%";

      // Create widget
      tvWidgetRef.current = new window.TradingView.widget({
        symbol: selectedMarket.symbol,
        interval: selectedResolution,
        fullscreen: false,
        container: chartWidgetContainerId,
        width,
        height,
        datafeed: datafeedRef.current,
        library_path: "/charting_library/",
        locale: "en",
        disabled_features: [
          "use_localstorage_for_settings",
          "save_chart_properties_to_local_storage",
          "study_templates",
          "display_market_status",
          "show_interval_dialog_on_key_press",
          "header_symbol_search",
          "header_compare",
        ],
        enabled_features: [
          "hide_left_toolbar_by_default",
          "two_character_bar_marks_labels",
        ],
        charts_storage_url: undefined,
        charts_storage_api_version: "1.1",
        client_id: "lumos_duel_chart",
        user_id: "lumos_user",
        theme: "dark",
        overrides: {
          "mainSeriesProperties.style": 1,
          "mainSeriesProperties.candleStyle.upColor": "#26a69a",
          "mainSeriesProperties.candleStyle.downColor": "#ef5350",
          "mainSeriesProperties.candleStyle.borderUpColor": "#26a69a",
          "mainSeriesProperties.candleStyle.borderDownColor": "#ef5350",
          "mainSeriesProperties.candleStyle.wickUpColor": "#26a69a",
          "mainSeriesProperties.candleStyle.wickDownColor": "#ef5350",
          "paneProperties.gridLinesMode": "none",
          "scalesProperties.textColor": "#d1d4dc",
          "scalesProperties.lineColor": "#485563",
        },
      });

      return new Promise((resolve) => {
        tvWidgetRef.current.onChartReady(() => {
          if (!isMountedRef.current) {
            resolve(false);
            return;
          }
          console.log("TradingView chart initialized successfully");
          setChartState((prev) => ({
            ...prev,
            loading: false,
            ready: true,
            error: null,
          }));

          resolve(true);
        });
      });
    } catch (error) {
      console.error("Error initializing TradingView widget:", error);
      setChartState((prev) => ({
        ...prev,
        loading: false,
        error: `Failed to initialize chart: ${error.message}. Please try refreshing the page.`,
        ready: false,
      }));
      return false;
    }
  }, [selectedMarket, isTradingViewLoaded, chartContainerId, chartWidgetContainerId, duelData, tradesData, computeChartSize, selectedResolution]);

  // Handle window resize
  const handleResize = useCallback(() => {
    const { width, height } = computeChartSize();

    if (chartContainerRef.current) {
      chartContainerRef.current.style.height = `${height}px`;
    }

    if (chartWidgetContainerRef.current) {
      chartWidgetContainerRef.current.style.width = "100%";
      chartWidgetContainerRef.current.style.height = "100%";
    }

    if (
      tvWidgetRef.current &&
      typeof tvWidgetRef.current.resize === "function"
    ) {
      tvWidgetRef.current.resize(width, height);
    }
  }, [computeChartSize]);

  // Extract duel and trades data from navigation state
  useEffect(() => {
    if (location.state) {
      const {
        duelData: duel,
        tradesData: trades,
        tradedMarkets: markets,
        duelId,
      } = location.state;
      // console.log("markets", markets);
      if (duel) setDuelData(duel);
      if (trades && trades.length > tradesData.length) setTradesData(trades);
      if (trades && trades.length > filteredTradesData.length) setFilteredTradesData(trades);
      if (markets) setTradedMarkets(markets);
      // Store duelId in sessionStorage as backup
      if (duelId) {
        sessionStorage.setItem("lastDuelId", duelId);
      }
    }
  }, [location.state, tradesData, filteredTradesData]);

  // Handle real-time trade updates from WebSocket
  const handleWebSocketTradesUpdate = useCallback(
    (updatedTrades) => {
      console.log("Received real-time trade updates:", updatedTrades.length);
      fetchTradeDetails();
    },
    [fetchTradeDetails]
  );

  // Use WebSocket hook for real-time duel updates
  useDuelWebSocket(duelData, handleWebSocketTradesUpdate);

  // Initialize component
  useEffect(() => {
    // If we have traded markets from duel data, prefer the last one
    const marketId =
      tradedMarkets.length > 0 ? tradedMarkets[0] : paramMarketId || null;
    const market = findMarketById(marketId);
    if (market) {
      setSelectedMarket(market);
    }
  }, [tradedMarkets, paramMarketId]);

  // Update datafeed with new duel/trade data
  useEffect(() => {
    if (datafeedRef.current) {
      console.log("Updated datafeed with new duel/trade data");
      fetchTradeDetails();
    }
  }, [fetchTradeDetails]);

  // Container ready state (removed unused state)

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Container is now checked in initializeChart function
  }, [computeChartSize]);

  // Simplified script loading with retry logic
  const loadTradingViewScripts = useCallback(() => {
    if (isTradingViewLoaded()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "/charting_library/charting_library.standalone.js";
      script.async = true;

      script.onload = () => {
        console.log("TradingView script loaded successfully");
        // Give it a moment to initialize
        setTimeout(() => {
          if (isTradingViewLoaded()) {
            resolve(true);
          } else {
            reject(
              new Error("TradingView library not available after script load")
            );
          }
        }, 500);
      };

      script.onerror = () => {
        reject(new Error("Failed to load TradingView script"));
      };

      // Check if already exists
      const existing = document.querySelector(
        'script[src*="charting_library"]'
      );
      if (!existing) {
        document.head.appendChild(script);
      } else {
        // Already exists, wait for it
        setTimeout(() => {
          if (isTradingViewLoaded()) {
            resolve(true);
          } else {
            reject(new Error("Existing TradingView script failed to load"));
          }
        }, 1000);
      }
    });
  }, [isTradingViewLoaded]);

  // Main initialization function with retry logic
  const initializeChart = useCallback(async () => {
    if (
      !isMountedRef.current ||
      !selectedMarket ||
      initializationPromiseRef.current
    ) {
      return;
    }

    initializationPromiseRef.current = (async () => {
      try {
        // Step 1: Load TradingView scripts if needed
        if (!isTradingViewLoaded()) {
          console.log("Loading TradingView scripts...");
          await loadTradingViewScripts();
        }

        // Step 2: Wait for container to be ready
        if (!chartContainerRef.current || !chartWidgetContainerRef.current) {
          throw new Error("Chart containers not available");
        }

        // Step 3: Initialize widget
        console.log("Initializing chart widget...");
        const success = await initializeTradingViewWidget();
        if (!success) {
          throw new Error("Widget initialization failed");
        }

        // Step 4: Set up resize handling
        window.addEventListener("resize", handleResize);

        return () => {
          window.removeEventListener("resize", handleResize);
        };
      } catch (error) {
        console.error("Chart initialization failed:", error);
        setChartState((prev) => ({
          ...prev,
          loading: false,
          error: `Chart initialization failed: ${error.message}`,
          retryCount: prev.retryCount + 1,
        }));

        // Auto-retry with exponential backoff (max 3 retries)
        if (chartState.retryCount < 3) {
          const delay = Math.min(
            1000 * Math.pow(2, chartState.retryCount),
            5000
          );
          setTimeout(() => {
            if (isMountedRef.current) {
              initializeChart();
            }
          }, delay);
        }
      } finally {
        initializationPromiseRef.current = null;
      }
    })();

    return initializationPromiseRef.current;
  }, [
    selectedMarket,
    isTradingViewLoaded,
    loadTradingViewScripts,
    initializeTradingViewWidget,
    handleResize,
    chartState.retryCount,
  ]);

  // Initialize chart when component mounts or dependencies change
  useEffect(() => {
    initializeChart();
  }, [initializeChart]);

  // Simplified resize handling
  useEffect(() => {
    if (!chartState.ready || !tvWidgetRef.current) return;

    const handleResize = () => {
      const { width, height } = computeChartSize();
      if (width > 0 && height > 0) {
        try {
          tvWidgetRef.current.resize(width, height);
        } catch (error) {
          console.warn("Failed to resize chart:", error);
        }
      }
    };

    // Debounced resize handler
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 100);
    };

    window.addEventListener("resize", debouncedResize);
    window.addEventListener("orientationchange", () => {
      setTimeout(handleResize, 300);
    });

    return () => {
      window.removeEventListener("resize", debouncedResize);
      window.removeEventListener("orientationchange", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [chartState.ready, computeChartSize]);

  // Handle market and resolution changes by updating widget instead of recreating
  useEffect(() => {
    if (!tvWidgetRef.current || !chartState.ready || !selectedMarket) return;

    try {
      // Update symbol if market changed
      if (tvWidgetRef.current.setSymbol) {
        tvWidgetRef.current.setSymbol(
          selectedMarket.symbol,
          selectedResolution,
          () => {
            console.log(
              "Chart updated to:",
              selectedMarket.symbol,
              selectedResolution
            );
          }
        );
      }

      // Update datafeed with new data
      if (datafeedRef.current) {
        datafeedRef.current.updateDuelData(duelData, tradesData);
      }
    } catch (error) {
      console.warn("Failed to update chart:", error);
      // If update fails, reinitialize chart
      initializeChart();
    }
  }, [
    selectedMarket,
    selectedResolution,
    chartState.ready,
    duelData,
    tradesData,
    initializeChart,
  ]);

  // // Handle resolution change
  // const handleResolutionChange = (resolution) => {
  //   setSelectedResolution(resolution);
  // };

  // Handle market change
  const handleMarketChange = (marketId) => {
    const market = findMarketById(marketId);
    if (market) {
      // Filter trades data for the new market
      const filteredTradesData = tradesData.filter(
        (trade) => parseInt(trade.market_id) === parseInt(marketId)
      );
      setFilteredTradesData(filteredTradesData);

      setSelectedMarket(market);
    }
  };

  // Handle back navigation to duel live page
  const handleBackToDuel = () => {
    // Try to get duelId from location state first, then sessionStorage
    const duelId =
      location.state?.duelId || sessionStorage.getItem("lastDuelId");

    if (duelId) {
      navigate(`/duel/live/${duelId}`);
    } else {
      console.log("No duelId found, using browser back navigation");
      navigate(-1); // fallback to browser back
    }
  };

  if (chartState.error) {
    return (
      <div className="dc-error">
        {chartState.error}
        {chartState.retryCount < 3 && (
          <button
            onClick={() => {
              setChartState((prev) => ({
                ...prev,
                error: null,
                retryCount: 0,
              }));
              initializeChart();
            }}
            className="dc-retry-btn"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <ChartErrorBoundary>
      <div className="dc-root">
        {/* Top Bar */}
        <div className="dc-top">
          <button className="dc-back-btn" onClick={handleBackToDuel}>
            ← BACK
          </button>
          <div className="dc-menu-container" onClick={() => navigate("/")}>
            <img className="dc-menu-logo" src={logo} alt="LUMOS" />
            <span className="dc-menu">LUMOS</span>
          </div>
        </div>

        {/* Duel Info */}
        {duelData && (
          <div className="dc-duel-info">
            <div className="dc-avatar-container">
              <div className="dc-avatar left">
                {duelData.challengerAvatarUrl ? (
                  <img
                    className="dc-avatar-img"
                    src={duelData.challengerAvatarUrl}
                    alt={duelData.challengerUsername}
                  />
                ) : (
                  <div className="dc-avatar-fallback">
                    {duelData.challengerUsername.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <span className="dc-vs">vs</span>
              <div className="dc-avatar right">
                {duelData.opponentAvatarUrl ? (
                  <img
                    className="dc-avatar-img"
                    src={duelData.opponentAvatarUrl}
                    alt={duelData.opponentUsername}
                  />
                ) : (
                  <div className="dc-avatar-fallback">
                    {duelData.opponentUsername.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="dc-controls">
          {/* Market Selector */}
          <select
            value={String(selectedMarket?.market_id) || ""}
            onChange={(e) => handleMarketChange(parseInt(e.target.value))}
            className="dc-select"
          >
            {/* Show traded markets first (if from duel) */}
            {tradedMarkets.length > 0 && (
              <optgroup label="Duel Markets">
                {marketsData
                  .filter(
                    (market) =>
                      market.status === "active" &&
                      tradedMarkets.includes(market.market_id)
                  )
                  .map((market) => (
                    <option
                      key={market.market_id}
                      value={market.market_id}
                      selected={market.symbol === selectedMarket?.symbol}
                    >
                      {market.symbol}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
        </div>

        {/* Chart Container */}
        {tradesData?.length > 0 ? (
          <div
            ref={chartContainerRef}
            id={chartContainerId}
            className="dc-chart-container"
            style={{
              width: "100%",
              maxWidth: "960px",
              height: "400px",
              margin: "0 auto",
              position: "relative",
            }}
          >
            <div
              ref={chartWidgetContainerRef}
              id={chartWidgetContainerId}
              className="dc-chart-inner"
              style={{ width: "100%", height: "100%" }}
            />
            {chartState.loading && (
              <div
                className="dc-loading"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#0a0a0a",
                  zIndex: 10,
                }}
              >
                {chartState.retryCount > 0
                  ? `Retrying... (${chartState.retryCount}/3)`
                  : "Loading chart..."}
              </div>
            )}
          </div>
        ) : (
          <div className="dc-chart-container" style={{ textAlign: "center", padding: "50px" }}>No Trades Yet</div>
        )}

        {/* Trade Markers Legend */}
        {duelData ? (
          <div className="dc-legend">
            <div className="dc-legend-title">Trade Markers</div>
            <div className="dc-legend-items">
              <div className="dc-legend-item">
                <div className="dc-avatar small">
                  {duelData.challengerAvatarUrl ? (
                    <img
                      className="dc-avatar-img"
                      src={duelData.challengerAvatarUrl}
                      alt={duelData.challengerUsername}
                    />
                  ) : (
                    <div className="dc-avatar-fallback">
                      {duelData.challengerUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <span>{duelData.challengerUsername} Trades</span>
              </div>
              <div className="dc-legend-item">
                <div className="dc-avatar small">
                  {duelData.opponentAvatarUrl ? (
                    <img
                      className="dc-avatar-img"
                      src={duelData.opponentAvatarUrl}
                      alt={duelData.opponentUsername}
                    />
                  ) : (
                    <div className="dc-avatar-fallback">
                      {duelData.opponentUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <span>{duelData.opponentUsername} Trades</span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Chart Info */}
        {chartState.ready && duelData && (
          <div className="dc-info">
            <div className="dc-info-grid">
              <div className="dc-info-item">
                <div className="dc-info-label">Duel Round</div>
                <div className="dc-info-value">{duelData.rounds}</div>
              </div>
              <div className="dc-info-item">
                <div className="dc-info-label">Recent Trades</div>
                <div className="dc-info-value">{filteredTradesData.length}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ChartErrorBoundary>
  );
};

export default DuelChart;
